/**
 * VideoCall Component - Private P2P Video Meeting for Game Sessions
 * * Handles local media access, UI controls (mute/video disable/devices),
 * and integrates with PeerConnection to manage the WebRTC session.
 */

import { PeerConnection } from '../../network/peer-connection';
import { MediaConnection } from 'peerjs'; // Need to import PeerJS type for clarity
// Note: Assuming GameMessage is not needed here as PeerConnection handles signaling

// ============================================
// VideoCall Class
// ============================================

export class VideoCall {
  private container: HTMLElement | null = null;
  private callElement: HTMLElement | null = null;
  private peerConnection: PeerConnection | null = null;
  
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private currentMediaCall: MediaConnection | null = null; // Track the active media connection

  private isMuted: boolean = false;
  private isVideoDisabled: boolean = false;
  private isOpen: boolean = false;
  
  private localVideoElement: HTMLVideoElement | null = null;
  private remoteVideoElement: HTMLVideoElement | null = null;
  private deviceModalElement: HTMLElement | null = null;
  
  // State for tracking the call status
  private callStatus: 'idle' | 'calling' | 'ringing' | 'connected' = 'idle';

  constructor(container?: HTMLElement) {
    this.container = container || null;
  }

  // ============================================
  // Public Methods
  // ============================================

  /**
   * Initialize the video call component
   */
  initialize(
    peerConnection: PeerConnection
  ): void {
    this.peerConnection = peerConnection;
    this.setupCallListeners(); // Listen for incoming calls
    this.render();
  }
  
  /**
   * Start the media stream and initiate the call to the peer. (Caller side)
   */
  public async startCall(): Promise<void> {
    if (!this.peerConnection || this.callStatus !== 'idle') {
      console.warn('Call already in progress or component not ready.');
      return;
    }

    this.callStatus = 'calling';
    this.updateToggleBtn('Calling...');

    try {
      // 1. Get local media streams (uses default or last selected devices)
      await this.getMediaStream();
      
      if (this.localStream) {
        // 2. Initiate the call using the PeerConnection wrapper
        this.currentMediaCall = this.peerConnection.callPeer(this.localStream);
        
        this.setupMediaConnectionListeners(this.currentMediaCall);
        this.openCallUI(true);
      }
    } catch (error) {
      console.error('Error starting video call:', error);
      alert('Could not start video call. Check camera/mic permissions.');
      this.resetCallState();
    }
  }

  /**
   * End the call and clean up
   */
  public endCall(): void {
    if (!this.peerConnection) return;

    // 1. Clean up local resources (stops media tracks)
    this.localStream?.getTracks().forEach(track => track.stop());
    
    // 2. Notify PeerConnection to close the MediaConnection
    this.peerConnection.endCall();
    
    // 3. Reset internal state and UI
    this.resetCallState();
    this.openCallUI(false);
  }
  
  /**
   * Destroy the component
   */
  public destroy(): void {
    this.endCall(); 
    
    // Remove PeerConnection listeners
    if (this.peerConnection) {
        this.peerConnection.onIncomingCall = null;
        this.peerConnection.onCallEnded = null;
    }
    
    if (this.callElement) {
        this.callElement.remove();
        this.callElement = null;
    }
    this.peerConnection = null;
  }
  
  /**
   * Toggle mute status
   */
  public toggleMute(): void {
    this.isMuted = !this.isMuted;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !this.isMuted;
      });
    }
    this.updateControlsUI();
  }

  /**
   * Toggle video disable status
   */
  public toggleVideo(): void {
    this.isVideoDisabled = !this.isVideoDisabled;
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach(track => {
        track.enabled = !this.isVideoDisabled;
      });
    }
    this.updateControlsUI();
  }
  
  // ============================================
  // Private Methods - WebRTC Setup & Handlers
  // ============================================

  /**
   * Setup listeners on the PeerConnection for incoming calls and connection status
   */
  private setupCallListeners(): void {
    if (!this.peerConnection) return;
    
    // --- 1. Handle incoming calls (Receiver side) ---
    this.peerConnection.onIncomingCall = async (call: MediaConnection) => {
        console.log('VideoCall component received incoming call.');
        
        if (this.callStatus !== 'idle') {
            call.close(); // Reject if busy
            return;
        }

        this.currentMediaCall = call;
        this.callStatus = 'ringing';
        this.updateToggleBtn('Incoming...');
        this.setupMediaConnectionListeners(call);
        
        // **ACTION: Prompt user to accept/reject call here.** // For simplicity, we auto-answer, but a real app needs user interaction.
        await this.answerCall();
    };
    
    // --- 2. Handle call end ---
    this.peerConnection.onCallEnded = () => {
        this.resetCallState();
        this.openCallUI(false);
        this.updateToggleBtn('Call Ended');
        setTimeout(() => this.updateToggleBtn(''), 3000);
    }
    
    // --- 3. Handle remote tracks (the core of receiving video) ---
    const nativeConn = this.peerConnection.getNativeConnection();
    if (nativeConn) {
        nativeConn.ontrack = (event: RTCTrackEvent) => {
            console.log('Remote track received:', event.track.kind);
            // This is the native WebRTC way to get the remote stream
            if (event.streams && event.streams[0]) {
                this.remoteStream = event.streams[0];
                if (this.remoteVideoElement) {
                    this.remoteVideoElement.srcObject = this.remoteStream;
                }
            }
        };
    }
  }

  /**
   * Answer an incoming call after getting local media. (Receiver side)
   */
  private async answerCall(): Promise<void> {
    try {
        await this.getMediaStream(); // Get local media
        
        if (this.currentMediaCall && this.localStream) {
            this.currentMediaCall.answer(this.localStream);
            this.callStatus = 'connected';
            this.openCallUI(true);
        } else {
            throw new Error("Missing call object or local stream.");
        }
    } catch (error) {
        console.error('Error answering call:', error);
        this.endCall();
    }
  }

  /**
   * Setup listeners on the active MediaConnection object
   */
  private setupMediaConnectionListeners(call: MediaConnection): void {
      // PeerJS fires 'stream' when the remote stream is fully available.
      // We rely primarily on `ontrack` (set in setupCallListeners) for the native WebRTC event, 
      // but keep this for robustness.
      call.on('stream', (remoteStream: MediaStream) => {
          console.log('PeerJS stream event received (Remote)');
          this.remoteStream = remoteStream;
          if (this.remoteVideoElement) {
              this.remoteVideoElement.srcObject = this.remoteStream;
          }
          this.callStatus = 'connected';
          this.updateToggleBtn('');
      });

      call.on('close', () => {
          // This fires when the remote peer hangs up
          this.peerConnection?.onCallEnded?.(); 
      });
      
      call.on('error', (err: Error) => {
          console.error('Media connection error:', err);
          this.peerConnection?.onCallEnded?.();
      });
  }

  /**
   * Request media streams from the user (handles device changes and initial setup)
   */
  private async getMediaStream(audioDeviceId?: string, videoDeviceId?: string): Promise<void> {
    const constraints: MediaStreamConstraints = {
      audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
      video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
    };

    if (this.localStream) {
        // Stop previous tracks before replacing
        this.localStream.getTracks().forEach(track => track.stop());
    }

    this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
    
    if (this.localVideoElement) {
      this.localVideoElement.srcObject = this.localStream;
      this.localVideoElement.muted = true;
    }
    
    // Maintain mute/disable state on new stream
    this.localStream.getAudioTracks().forEach(track => track.enabled = !this.isMuted);
    this.localStream.getVideoTracks().forEach(track => track.enabled = !this.isVideoDisabled);
  }

  // ============================================
  // Private Methods - UI & Utilities
  // ============================================
  
  /**
   * Render the video call interface
   */
  public render(): void {
    if (this.callElement) {
      this.callElement.remove();
    }
    
    this.callElement = document.createElement('div');
    this.callElement.className = 'video-call-overlay';
    
    this.callElement.innerHTML = `
        <div class="video-call-container hidden">
            <div class="video-window remote-video-container">
                <video class="remote-video" autoplay playsinline></video>
                <div class="video-label">Remote Player</div>
            </div>
            
            <div class="video-window local-video-container">
                <video class="local-video" autoplay playsinline muted></video>
                <div class="video-label">You</div>
            </div>
            
            <div class="video-call-controls">
                <button class="control-btn toggle-audio-btn" aria-label="Toggle Audio Mute">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M12 2C9.79 2 8 3.79 8 6v6c0 2.21 1.79 4 4 4s4-1.79 4-4V6c0-2.21-1.79-4-4-4zm-4 10v1c0 2.48 2.02 4.5 4.5 4.5s4.5-2.02 4.5-4.5v-1h-1v1c0 1.93-1.57 3.5-3.5 3.5S8.5 13.93 8.5 12V10h-1v2zM19 12h-2c0 2.82-2.18 5.16-5 5.48V21h-2v-3.52c-2.82-.32-5-2.66-5-5.48H5c.2 3.03 2.5 5.57 5.5 6.16V22h3v-3.84c3-.59 5.3-3.13 5.5-6.16z"/>
                    </svg>
                </button>

                <button class="control-btn toggle-video-btn" aria-label="Toggle Video">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                    </svg>
                </button>

                <button class="control-btn device-select-btn" aria-label="Select Devices">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.38-1.09-.72-1.71-.99L14 3.76c-.09-.27-.33-.44-.61-.44h-4c-.28 0-.52.17-.61.44L8.03 6.01c-.62.27-1.19.61-1.71.99l-2.49-1c-.22-.08-.49 0-.61.22l-2 3.46c-.12.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.38 1.09.72 1.71.99l.34 2.51c.09.27.33.44.61.44h4c.28 0 .52-.17.61-.44l.34-2.51c.62-.27 1.19-.61 1.71-.99l2.49 1c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/>
                    </svg>
                </button>

                <button class="control-btn end-call-btn" aria-label="End Call">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M21 6.76L19.24 5H18c-.28 0-.5.22-.5.5s.22.5.5.5h1.24l1.52 1.52.01.01V12h2v-4.76l-1.99-1.99zM8 12c-2.21 0-4 1.79-4 4s1.79 4 4 4c.66 0 1.29-.16 1.85-.45l4.33 4.33c.19.19.44.29.71.29s.52-.1.71-.29c.39-.39.39-1.02 0-1.41L10.36 18H16v-2h-3.52l4.89-4.89L21.12 18H22v-6H8zm12.99-6.99L4.99 21.01 3.98 20l17.02-17.02L20.99 5z"/>
                    </svg>
                </button>
            </div>
            
            <div class="device-modal hidden">
                ${this.renderDeviceSelection()}
            </div>
        </div>
        
        <button class="video-call-toggle-btn" aria-label="Start Video Call">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
            </svg>
            <span class="call-status-badge"></span>
        </button>
    `;

    const target = this.container || document.body;
    target.appendChild(this.callElement);

    
    this.localVideoElement = this.callElement.querySelector('.local-video');
    this.remoteVideoElement = this.callElement.querySelector('.remote-video');
    this.deviceModalElement = this.callElement.querySelector('.device-modal');
    
    this.setupEventListeners();
  }
  
  /**
   * Opens or closes the main call UI window
   */
  private openCallUI(open: boolean): void {
      this.isOpen = open;
      const container = this.callElement?.querySelector('.video-call-container');
      if (container) {
          container.classList.toggle('hidden', !open);
      }
      
      if (open) {
          this.updateControlsUI();
      }
  }

  /**
   * Update the call status badge on the toggle button
   */
  private updateToggleBtn(text: string): void {
      const badge = this.callElement?.querySelector('.call-status-badge') as HTMLElement;
      if (badge) {
          badge.textContent = text;
          badge.classList.toggle('visible', text !== '');
      }
  }
  
  /**
   * Apply selected devices and update tracks on the PeerConnection
   */
  private applyNewDevices(audioDeviceId: string, videoDeviceId: string): void {
      this.getMediaStream(audioDeviceId, videoDeviceId)
        .then(() => {
            if (!this.peerConnection || !this.localStream) return;
            
            const nativeConn = this.peerConnection.getNativeConnection();
            if (!nativeConn) return;

            // Find and replace old tracks with the new ones
            this.localStream.getTracks().forEach(newTrack => {
                const sender = nativeConn.getSenders().find(s => s.track?.kind === newTrack.kind);
                
                if (sender) {
                    sender.replaceTrack(newTrack).catch(e => console.error('Failed to replace track:', e));
                } else {
                    // This is a fallback if the sender doesn't exist (e.g., initial connection)
                    nativeConn.addTrack(newTrack, this.localStream!);
                }
            });
            
            this.deviceModalElement?.classList.add('hidden');
        })
        .catch(error => {
            console.error('Failed to apply new devices:', error);
            alert('Failed to switch devices. Check permissions.');
        });
  }

  /**
   * Reset all internal state and streams
   */
  private resetCallState(): void {
      this.callStatus = 'idle';
      this.currentMediaCall = null;
      this.localStream = null;
      this.remoteStream = null;
      if (this.localVideoElement) this.localVideoElement.srcObject = null;
      if (this.remoteVideoElement) this.remoteVideoElement.srcObject = null;
  }
  
  // --- UI/Device methods (same as previous response, but for completeness) ---
  
  private updateControlsUI(): void {
      const audioBtn = this.callElement?.querySelector('.toggle-audio-btn');
      const videoBtn = this.callElement?.querySelector('.toggle-video-btn');
      
      if (audioBtn) {
          // Toggle MUTE path
          const audioSvg = audioBtn.querySelector('svg path');
          if (audioSvg) {
              audioSvg.setAttribute('d', this.isMuted 
                  ? 'M12 2C9.79 2 8 3.79 8 6v6c0 2.21 1.79 4 4 4s4-1.79 4-4V6c0-2.21-1.79-4-4-4zm-4 10v1c0 2.48 2.02 4.5 4.5 4.5s4.5-2.02 4.5-4.5v-1h-1v1c0 1.93-1.57 3.5-3.5 3.5S8.5 13.93 8.5 12V10h-1v2zM19 12h-2c0 2.82-2.18 5.16-5 5.48V21h-2v-3.52c-2.82-.32-5-2.66-5-5.48H5c.2 3.03 2.5 5.57 5.5 6.16V22h3v-3.84c3-.59 5.3-3.13 5.5-6.16z M21 6.76L19.24 5H18c-.28 0-.5.22-.5.5s.22.5.5.5h1.24l1.52 1.52.01.01V12h2v-4.76l-1.99-1.99zM8 12c-2.21 0-4 1.79-4 4s1.79 4 4 4c.66 0 1.29-.16 1.85-.45l4.33 4.33c.19.19.44.29.71.29s.52-.1.71-.29c.39-.39.39-1.02 0-1.41L10.36 18H16v-2h-3.52l4.89-4.89L21.12 18H22v-6H8zm12.99-6.99L4.99 21.01 3.98 20l17.02-17.02L20.99 5z' // Combined mic + slash path (for visual feedback)
                  : 'M12 2C9.79 2 8 3.79 8 6v6c0 2.21 1.79 4 4 4s4-1.79 4-4V6c0-2.21-1.79-4-4-4zm-4 10v1c0 2.48 2.02 4.5 4.5 4.5s4.5-2.02 4.5-4.5v-1h-1v1c0 1.93-1.57 3.5-3.5 3.5S8.5 13.93 8.5 12V10h-1v2zM19 12h-2c0 2.82-2.18 5.16-5 5.48V21h-2v-3.52c-2.82-.32-5-2.66-5-5.48H5c.2 3.03 2.5 5.57 5.5 6.16V22h3v-3.84c3-.59 5.3-3.13 5.5-6.16z' // Regular microphone path
              );
          }
      }

      if (videoBtn) {
          // Toggle VIDEO path
          const videoSvg = videoBtn.querySelector('svg path');
          if (videoSvg) {
              videoSvg.setAttribute('d', this.isVideoDisabled
                  ? 'M21 6.76L19.24 5H18c-.28 0-.5.22-.5.5s.22.5.5.5h1.24l1.52 1.52.01.01V12h2v-4.76l-1.99-1.99zM8 12c-2.21 0-4 1.79-4 4s1.79 4 4 4c.66 0 1.29-.16 1.85-.45l4.33 4.33c.19.19.44.29.71.29s.52-.1.71-.29c.39-.39.39-1.02 0-1.41L10.36 18H16v-2h-3.52l4.89-4.89L21.12 18H22v-6H8zm12.99-6.99L4.99 21.01 3.98 20l17.02-17.02L20.99 5z M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z' // Combined video + slash path
                  : 'M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z' // Regular video path
              );
          }
      }
  }

  private toggleDeviceModal(open: boolean): void {
      if (this.deviceModalElement) {
          this.deviceModalElement.classList.toggle('hidden', !open);
          if (open) {
              this.populateDevices();
          }
      }
  }

  private renderDeviceSelection(): string {
    return `
      <div class="device-modal-content">
        <h3>Select Devices</h3>
        <div class="device-group">
          <label for="audio-input-select">Microphone:</label>
          <select id="audio-input-select" class="device-select"></select>
        </div>
        <div class="device-group">
          <label for="video-input-select">Camera:</label>
          <select id="video-input-select" class="device-select"></select>
        </div>
        <button class="apply-devices-btn">Apply Changes</button>
        <button class="close-modal-btn">Close</button>
      </div>
    `;
  }
  
  private async populateDevices(): Promise<void> {
    if (!this.deviceModalElement) return;
    
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputSelect = this.deviceModalElement.querySelector('#audio-input-select') as HTMLSelectElement;
        const videoInputSelect = this.deviceModalElement.querySelector('#video-input-select') as HTMLSelectElement;

        // Clear existing options
        audioInputSelect.innerHTML = '';
        videoInputSelect.innerHTML = '';

        devices.forEach(device => {
            if (device.kind === 'audioinput' || device.kind === 'videoinput') {
                const option = document.createElement('option');
                option.value = device.deviceId;
                option.text = device.label || `${device.kind} (${device.deviceId.substring(0, 8)})`;
                
                if (device.kind === 'audioinput') {
                    audioInputSelect.appendChild(option);
                } else {
                    videoInputSelect.appendChild(option);
                }
            }
        });

    } catch (error) {
        console.error('Error enumerating devices. Permission may be missing.', error);
    }
  }

  private setupEventListeners(): void {
      if (!this.callElement) return;

      // Start/Toggle button
      this.callElement.querySelector('.video-call-toggle-btn')?.addEventListener('click', () => {
          if (this.callStatus === 'idle') {
              this.startCall();
          } else if (this.callStatus === 'connected') {
              this.openCallUI(!this.isOpen); // Toggle visibility
          } else {
              console.log('Call is currently ringing or connecting...');
          }
      });
      
      // Control buttons
      this.callElement.querySelector('.toggle-audio-btn')?.addEventListener('click', () => this.toggleMute());
      this.callElement.querySelector('.toggle-video-btn')?.addEventListener('click', () => this.toggleVideo());
      this.callElement.querySelector('.end-call-btn')?.addEventListener('click', () => this.endCall());
      
      // Device selection button
      this.callElement.querySelector('.device-select-btn')?.addEventListener('click', () => {
          this.toggleDeviceModal(true);
      });
      
      // Device modal event listeners
      this.deviceModalElement?.querySelector('.apply-devices-btn')?.addEventListener('click', () => {
          const audioInputSelect = this.deviceModalElement!.querySelector('#audio-input-select') as HTMLSelectElement;
          const videoInputSelect = this.deviceModalElement!.querySelector('#video-input-select') as HTMLSelectElement;
          
          this.applyNewDevices(audioInputSelect.value, videoInputSelect.value);
      });
      
      this.deviceModalElement?.querySelector('.close-modal-btn')?.addEventListener('click', () => {
          this.toggleDeviceModal(false);
      });
  }
}